import type { TFunction } from 'i18next';
import type { RoleName } from '../../constants/roles';
import type { ServerNotification } from './types';

export interface Described {
    text: string;
    /** Route to act in; null when the role may not open it. */
    to: string | null;
    linkLabel: string;
}

const num = (v: string | number | boolean | null | undefined): number => (typeof v === 'number' ? v : Number(v ?? 0));

/** Words for the notification kinds, translated; the API sends data, never sentences. */
export const describeNotification = (
    n: ServerNotification,
    t: TFunction<'notifications'>,
    ctx: { role: RoleName | undefined; restaurantId: string | null; pending: number }
): Described => {
    const canSeeLocations = ctx.role !== 'Staff';
    switch (n.kind) {
        case 'orders_waiting': {
            const count = ctx.pending || n.count || 1;
            return { text: t('item.ordersWaiting', { count }), to: '/orders', linkLabel: t('link.orders') };
        }
        case 'orders_overdue': {
            const oldestId = n.data.oldestOrderId;
            return {
                text: t('item.ordersOverdue', { count: n.count ?? 1, minutes: num(n.data.minutes), number: String(n.data.oldestOrderNumber ?? '') }),
                to: typeof oldestId === 'string' ? `/orders/${oldestId}` : '/orders',
                linkLabel: t(typeof oldestId === 'string' ? 'link.order' : 'link.orders'),
            };
        }
        case 'ordering_paused':
            return {
                text: t('item.orderingPaused', { location: n.locationName ?? '' }),
                to: canSeeLocations && n.locationId ? `/locations/${n.locationId}` : null,
                linkLabel: t('link.location'),
            };
        case 'plan_limit': {
            const limit = String(n.data.limit);
            const text = t(n.data.level === 'reached' ? 'item.limitReached' : 'item.limitClose', {
                used: num(n.data.used),
                max: num(n.data.max),
                what: t(`limit.${limit}`, { defaultValue: limit }),
            });
            if (n.target === 'team') return { text, to: '/users', linkLabel: t('link.team') };
            if (n.target === 'venue') return { text, to: ctx.restaurantId ? `/restaurants/edit/${ctx.restaurantId}` : null, linkLabel: t('link.venue') };
            return { text, to: '/plan', linkLabel: t('link.plan') };
        }
        case 'trial': {
            const days = num(n.data.daysLeft);
            const text = n.data.level === 'ended' ? t('item.trialEnded') : days === 0 ? t('item.trialToday') : t('item.trialEnding', { count: days });
            return { text, to: '/plan', linkLabel: t('link.plan') };
        }
    }
};
